<template>
  <div>
    <div>
      <div class="flex flex-column">
        <label class="mt-2">{{ label ? label : t("components.shared.Calculator.value") }}</label>

        <LISAInputNumber
          ref="inputReference"
          :model-value="modelValue"
          :pt="{
            pcInputText: {
              root: {
                autofocus,
              },
            },
          }"
          :class="inputClass"
          class="mt-1 CustomCalculatorInput"
          :min-fraction-digits="minFractionDigits"
          :max-fraction-digits="maxFractionDigits"
          :locale="locale"
          :right-align="true"
          @keypress.enter="handleInputEvent"
          @keydown="handleKeyUp"
          @blur="handleInputEvent"
          @keydown.enter="$emit('createDatapoint')"
        />
        <slot name="input-append"></slot>
      </div>
    </div>

    <div class="mt-2 parent" :style="parentConfig">
      <template v-for="(row, rowIndex) in layout" :key="`${rowIndex}|row`">
        <template v-for="(key, keyIndex) in row.split(' ')" :key="`key|${keyIndex}`">
          <div v-if="!shouldSkip(row, keyIndex)" :style="getKeyPosition(rowIndex, keyIndex, key)">
            <LISAButton
              v-if="!(key === ',' && allowComma === false)"
              class="p-button-outlined w-100"
              :data-testid="`CalculatorButton${key}`"
              @click="handleKeyPress(key)"
            >
              <div class="text-center w-100">
                <i v-if="formatKeyLabel(key).type === 'icon'" :class="formatKeyLabel(key).value" />
                <span v-else-if="formatKeyLabel(key).type === 'text'">{{ key }}</span>
              </div>
            </LISAButton>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, onMounted, ref, watch } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { locale } from "@/language/translation";
import { NumberParser } from "@/functions/numberParser";
import LISAInputNumber from "../primevueWrapper/LISAInputNumber.vue";

const props = defineProps({
  label: {
    type: String,
    required: false,
    default: null,
  },
  maxFractionDigits: {
    type: Number,
    required: false,
    default: 20,
  },
  minFractionDigits: {
    type: Number,
    required: false,
    default: 2,
  },
  allowComma: {
    type: Boolean,
    required: false,
    default: true,
  },
  modelValue: {
    type: Number,
    required: false,
    default: 0,
  },
  state: {
    type: Boolean,
    required: false,
    default: null,
  },
  autofocus: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const emit = defineEmits(["update:modelValue", "createDatapoint"]);
const inputReference = ref<{ $el?: HTMLInputElement }>();
const formatter = computed(() => {
  return new Intl.NumberFormat(locale.value, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 20,
    useGrouping: false,
    notation: "standard",
  });
});

const parser = computed(() => {
  return new NumberParser(locale.value);
});

const layout = ref(["-/+  AC", "7 8 9", "4 5 6", "1 2 3", "0 , backspace"]);
const maxColumn = 3;

const getKeyPosition = (rowIndex: number, keyIndex: number, key: string) => {
  if (key === "-/+") {
    return {
      gridColumn: `${keyIndex + 1} / span 2`,
      gridRow: `${rowIndex + 1}`,
    };
  }

  return {
    gridColumn: `${keyIndex + 1}`,
    gridRow: `${rowIndex + 1}`,
  };
};

const originalValue = ref(props.modelValue ?? 0);
/**
 * emitting because it could ne undefined or null.
 * we ensure that the initial value is a number!
 */
emit("update:modelValue", originalValue.value);

const numberPart = ref<[string, string]>([
  originalValue.value.toString().split(".")[0] ?? "",
  originalValue.value.toString().split(".")[1] ?? "",
]);
const formattedNumberParts = computed(() =>
  Number(`${numberPart.value[0] ?? 0}${numberPart.value[0] ? "." : ""}${numberPart.value[1]}`),
);

const value = computed({
  get() {
    return formatter.value.format(formattedNumberParts.value);
  },
  set(v: string) {
    const parsed = parser.value.parse(v).toString();
    numberPart.value = [parsed.split(".")[0] ?? "", parsed.split(".")[1] ?? ""];
  },
});

watch(formattedNumberParts, () => {
  emit("update:modelValue", formattedNumberParts.value);
});

const decimalPart = ref(false);
const handleKeyPress = (key: string) => {
  const num = Number(key);
  switch (key) {
    case "AC":
      numberPart.value = ["", ""];
      decimalPart.value = false;
      break;
    case "-/+":
      if (numberPart.value[0].startsWith("-")) {
        numberPart.value[0] = numberPart.value[0].substring(1);
      } else {
        numberPart.value[0] = `-${numberPart.value[0] || "0"}`;
      }
      break;

    case ",":
      if (props.allowComma === true) {
        decimalPart.value = true;
      } else {
        return;
      }
      break;
    case "backspace":
      if (numberPart.value[1].length > 0) {
        numberPart.value[1] = numberPart.value[1].slice(0, -1);
      } else if (numberPart.value[0].length > 0) {
        if (numberPart.value[0].slice(0, -1) === "-") {
          numberPart.value[0] = "";
        } else {
          numberPart.value[0] = numberPart.value[0].slice(0, -1);
        }
      }
      if (numberPart.value[1].length === 0) {
        decimalPart.value = false;
      }
      break;
    default:
      if (!Number.isNaN(num)) {
        if (decimalPart.value) {
          numberPart.value[1] = `${numberPart.value[1]}${key}`;
        } else {
          numberPart.value[0] = `${numberPart.value[0]}${key}`;
        }
      }
  }
};

const formatKeyLabel = (key: string) => {
  switch (key) {
    case "backspace":
      return {
        type: "icon",
        value: "fas fa-backspace",
      };
    default:
      return {
        type: "text",
        value: key,
      };
  }
};
const shouldSkip = (row: string, keyIndex: number) => {
  const keys = row.split(" ");

  if (keys[keyIndex - 1] === "-/+") {
    return true;
  }

  return false;
};
const parentConfig = {
  gridTemplateRows: `repeat(${layout.value.length}, auto)`,
  gridTemplateColumns: `repeat(${maxColumn}, 1fr)`,
};

const inputClass = computed(() => {
  const classObj = {
    "p-invalid": props.state === false,
    "p-valid": props.state === true,
    "text-right": true,
  };
  const keys = Object.keys(classObj) as (keyof typeof classObj)[];

  return keys.filter((key) => classObj[key]).join(" ");
});

function handleKeyUp(e: KeyboardEvent) {
  e.preventDefault();
  handleKeyPress(e.key.toLowerCase());
}

function handleInputEvent(e: any) {
  if ("preventDefault" in e) {
    e.preventDefault();
  }
  if ("target" in e) {
    value.value = e.target.value;
  } else if ("value" in e) {
    value.value = e.value;
  }
}

onMounted(() => {
  if (props.autofocus) {
    setTimeout(() => {
      inputReference.value?.$el?.focus();
    }, 100);
  }
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.parent {
  display: grid;
  grid-column-gap: 4px;
  grid-row-gap: 4px;
}

.CustomCalculatorInput {
  text-align: right;
}
</style>
